/**
 * Bar/beat positions under the editor meter (W1-01 core-time-math; DESIGN.md §4.15: bar lines, PageUp/PageDown
 * bar seeking, alignment rows and metronome accents follow the meter "so a 3/4 or 7/8 section stays correct").
 *
 * The meter is the model's `Timing.meter` (core/model/types.ts `MeterMark[]`: from beat `t` on, a bar holds
 * `beatsPerBar` beats). This module only needs that structure, so it takes `MeterMarkLike` and does not import
 * the model. Rules, exact in rational arithmetic:
 *   - marks are used in beat order (the list order does not matter); of several marks at the same beat the one
 *     that comes last in the list wins;
 *   - `beatsPerBar` is read as its best rational approximation with denominator <= 1920 (3.5 = 7/2 for 7/8 in
 *     quarter-note beats); a value that is not finite, not positive, or rounds to 0 means the default 4;
 *   - when no mark is at or before beat 0 (including an empty list) the default meter, 4 beats per bar, starts at
 *     beat 0, which is also the model default `[{ t: 0, beatsPerBar: 4 }]`;
 *   - every mark starts a new bar at its beat; a mark inside a bar cuts that bar short;
 *   - the first meter extends backwards before its mark (lead-in beats);
 *   - bars are numbered from 1 for the bar that contains beat 0; bars before it are 0, -1, ...
 * Beat-in-bar is 1-based; the remaining fraction of a beat is reported separately (bar 3 . beat 2 + 1/4).
 * Bar starts are returned as Beats with the computed-result overflow policy of core/time/beat.ts (a start whose
 * reduced denominator would exceed 2^32 - 1 is rounded to 1/80640); starts outside the beat range throw.
 */

import { Rat, type BigRational } from '../math/rational';
import { defined } from '../util/assert';
import { beatFromRat, beatToRat } from './beat';
import type { Beat } from './types';

/** Meter used where no mark applies, and for invalid `beatsPerBar` values. */
export const DEFAULT_BEATS_PER_BAR = 4;
/** Largest denominator of a meter length in beats (the finest beat snap). */
const METER_MAX_DEN = 1920;

/** Structural view of core/model `MeterMark`: from beat `t` on, a bar holds `beatsPerBar` beats. */
export interface MeterMarkLike {
  readonly t: Beat;
  readonly beatsPerBar: number;
}

/** Position of a beat inside the meter: 1-based bar and beat-in-bar, plus the fraction of the beat. */
export interface BarBeatParts {
  readonly bar: number;
  readonly beat: number;
  /** Remaining fraction of a beat as [numerator, denominator], or null on a whole beat. */
  readonly frac: readonly [n: number, d: number] | null;
}

/** A meter compiled for repeated queries (gutter bar lines, PageUp/PageDown, metronome accents). */
export interface MeterMap {
  /** The effective marks in beat order, after de-duplication and with the implicit default mark, if any. */
  readonly marks: readonly MeterMarkLike[];
  /** Bar / beat-in-bar / fraction of b. */
  parts(b: Beat): BarBeatParts;
  /** Bar number (as in `parts`) of the bar containing b. */
  barAt(b: Beat): number;
  /** Length in beats of a full bar of the meter in force at b (the effective value, e.g. 3.5). */
  beatsPerBarAt(b: Beat): number;
  /** First beat of the bar containing b. */
  barStartAt(b: Beat): Beat;
  /** First beat of bar number `bar` (any integer; 1 is the bar containing beat 0). Throws RangeError otherwise. */
  barStart(bar: number): Beat;
  /** First bar start strictly after b (PageDown). */
  nextBarStart(b: Beat): Beat;
  /** Last bar start strictly before b (PageUp: from inside a bar to its start, from a bar line to the bar before). */
  prevBarStart(b: Beat): Beat;
  /** Whether b is exactly the first beat of a bar (bar lines, metronome accents). */
  isBarStart(b: Beat): boolean;
  /**
   * Bar starts s with from <= s <= to in ascending order, at most `limit` of them (default: no limit). Empty when
   * from > to.
   */
  barStartsBetween(from: Beat, to: Beat, limit?: number): Beat[];
}

interface Segment {
  /** Start beat of the segment (the mark's beat). */
  readonly t: BigRational;
  /** Bar length in beats, > 0. */
  readonly len: BigRational;
  /** Index of the segment's first bar, counted from the first segment's first bar (0). */
  readonly firstBar: bigint;
  readonly mark: MeterMarkLike;
}

/** Effective bar length of a `beatsPerBar` value (see the module header). */
export function meterLength(beatsPerBar: number | undefined): BigRational {
  if (beatsPerBar === undefined || !Number.isFinite(beatsPerBar) || beatsPerBar <= 0) {
    return Rat.of(DEFAULT_BEATS_PER_BAR);
  }
  const r = Rat.approx(Rat.fromF64(beatsPerBar), METER_MAX_DEN);
  return r.num > 0n ? r : Rat.of(DEFAULT_BEATS_PER_BAR);
}

function ceilDiv(a: BigRational, b: BigRational): bigint {
  // ceil(a / b) for b > 0
  const q = Rat.div(a, b);
  const f = Rat.floor(q);
  return q.den === 1n ? f : f + 1n;
}

function toBarNumber(index: bigint): number {
  const n = Number(index);
  if (!Number.isSafeInteger(n)) throw new RangeError(`meter: bar number ${index.toString()} is out of range`);
  return n;
}

/** Compiles a meter (any order, possibly empty) for repeated queries. */
export function createMeterMap(meter: readonly MeterMarkLike[]): MeterMap {
  // Beat order, stable, so that of equal beats the later list entry comes last and wins.
  const sorted = meter
    .map((mark, i) => ({ mark, i, t: beatToRat(mark.t) }))
    .sort((x, y) => Rat.cmp(x.t, y.t) || x.i - y.i);
  const effective: { readonly mark: MeterMarkLike; readonly t: BigRational }[] = [];
  for (const m of sorted) {
    const last = effective[effective.length - 1];
    if (last !== undefined && Rat.eq(last.t, m.t)) effective[effective.length - 1] = m;
    else effective.push(m);
  }
  const first = effective[0];
  if (first === undefined || Rat.sign(first.t) > 0) {
    const zero: Beat = { w: 0, n: 0, d: 1 };
    effective.unshift({ mark: Object.freeze({ t: zero, beatsPerBar: DEFAULT_BEATS_PER_BAR }), t: Rat.ZERO });
  }

  const segments: Segment[] = [];
  let firstBar = 0n;
  for (const [i, e] of effective.entries()) {
    const len = meterLength(e.mark.beatsPerBar);
    segments.push({ t: e.t, len, firstBar, mark: e.mark });
    const next = effective[i + 1];
    if (next !== undefined) firstBar += ceilDiv(Rat.sub(next.t, e.t), len);
  }

  /** Last segment whose start is <= x, or the first segment when x precedes all of them. */
  const segmentAt = (x: BigRational): Segment => {
    let lo = 0;
    let hi = segments.length - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      const s = segments[mid];
      if (s !== undefined && Rat.cmp(s.t, x) <= 0) lo = mid;
      else hi = mid - 1;
    }
    return defined(segments[lo], 'meter: a compiled meter always has a segment');
  };

  /** Segment holding bar index `index` (the first segment for indices before it). */
  const segmentOfIndex = (index: bigint): Segment => {
    let lo = 0;
    let hi = segments.length - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      const s = segments[mid];
      if (s !== undefined && s.firstBar <= index) lo = mid;
      else hi = mid - 1;
    }
    return defined(segments[lo], 'meter: a compiled meter always has a segment');
  };

  /** Bar index (from the first segment) of x, the segment, and the bar's start. */
  const locate = (x: BigRational): { readonly index: bigint; readonly seg: Segment; readonly start: BigRational } => {
    const seg = segmentAt(x);
    const k = Rat.floor(Rat.div(Rat.sub(x, seg.t), seg.len));
    return { index: seg.firstBar + k, seg, start: Rat.add(seg.t, Rat.mul(Rat.of(k), seg.len)) };
  };

  const startOfIndex = (index: bigint): BigRational => {
    const seg = segmentOfIndex(index);
    return Rat.add(seg.t, Rat.mul(Rat.of(index - seg.firstBar), seg.len));
  };

  const originIndex = locate(Rat.ZERO).index;

  const parts = (b: Beat): BarBeatParts => {
    const x = beatToRat(b);
    const { index, start } = locate(x);
    const within = Rat.sub(x, start);
    const whole = Rat.floor(within);
    const frac = Rat.sub(within, Rat.of(whole));
    return {
      bar: toBarNumber(index - originIndex + 1n),
      beat: Number(whole) + 1,
      frac: frac.num === 0n ? null : [Number(frac.num), Number(frac.den)],
    };
  };

  const barStartsBetween = (from: Beat, to: Beat, limit = Number.POSITIVE_INFINITY): Beat[] => {
    const out: Beat[] = [];
    const lo = beatToRat(from);
    const hi = beatToRat(to);
    if (Rat.cmp(lo, hi) > 0) return out;
    const first = locate(lo);
    let index = Rat.cmp(first.start, lo) < 0 ? first.index + 1n : first.index;
    while (out.length < limit) {
      const s = startOfIndex(index);
      if (Rat.cmp(s, hi) > 0) break;
      out.push(beatFromRat(s));
      index += 1n;
    }
    return out;
  };

  const map: MeterMap = {
    marks: Object.freeze(segments.map((s) => s.mark)),
    parts,
    barAt: (b) => toBarNumber(locate(beatToRat(b)).index - originIndex + 1n),
    beatsPerBarAt: (b) => Rat.toF64(segmentAt(beatToRat(b)).len),
    barStartAt: (b) => beatFromRat(locate(beatToRat(b)).start),
    barStart: (bar) => {
      if (!Number.isSafeInteger(bar)) throw new RangeError(`meter: bar must be an integer (got ${String(bar)})`);
      return beatFromRat(startOfIndex(BigInt(bar) - 1n + originIndex));
    },
    nextBarStart: (b) => beatFromRat(startOfIndex(locate(beatToRat(b)).index + 1n)),
    prevBarStart: (b) => {
      const x = beatToRat(b);
      const { index, start } = locate(x);
      return beatFromRat(Rat.cmp(start, x) < 0 ? start : startOfIndex(index - 1n));
    },
    isBarStart: (b) => {
      const x = beatToRat(b);
      return Rat.eq(locate(x).start, x);
    },
    barStartsBetween,
  };
  return Object.freeze(map);
}

/** Bar / beat / fraction of b for a constant meter counted from beat 0 (default 4 beats per bar). */
export function barBeatParts(b: Beat, beatsPerBar?: number): BarBeatParts {
  const len = meterLength(beatsPerBar);
  const x = beatToRat(b);
  const bar = Rat.floor(Rat.div(x, len));
  const within = Rat.sub(x, Rat.mul(Rat.of(bar), len));
  const whole = Rat.floor(within);
  const frac = Rat.sub(within, Rat.of(whole));
  return {
    bar: toBarNumber(bar + 1n),
    beat: Number(whole) + 1,
    frac: frac.num === 0n ? null : [Number(frac.num), Number(frac.den)],
  };
}

/** A compiled meter, or a mark list compiled on the fly (callers in hot loops compile once with createMeterMap). */
export type MeterInput = MeterMap | readonly MeterMarkLike[];

export function toMeterMap(meter: MeterInput): MeterMap {
  return 'parts' in meter ? meter : createMeterMap(meter);
}

/** Bar / beat / fraction of b under a meter with changes (`Timing.meter`). */
export function barBeatPartsForMeter(b: Beat, meter: MeterInput): BarBeatParts {
  return toMeterMap(meter).parts(b);
}
